import { Chip, Tooltip } from '@mui/material';
import React from 'react';
import {
  Datagrid,
  DateField,
  Filter,
  FunctionField,
  List,
  NumberField,
  SearchInput,
  SelectInput,
  TextField,
} from 'react-admin';
import ListPagination from '../../components/ListPagination';
import { formatCpf } from '../../lib';
import Permission from '../../components/Permission';
import { PERMISSIONS } from '../../constants';
import ChargeSessionsSummary from './ChargeSessionsSummary';
import {
  CHARGE_SESSION_STATUS_CHOICES,
  CHARGE_SESSION_STATUS_COLOR,
  translateNotPointedReason,
  translateStatus,
} from './statusLabels';

/**
 * Sessões de recarga elétrica (InCharge).
 *
 * Existe para responder, sem psql, a pergunta que o piloto vai gerar: "escaneei o carregador
 * e não pontuou, por quê". Cada linha mostra o desfecho e o motivo; o card "Pontuadas" é o
 * termômetro da integração — se cair, ou o webhook do parceiro parou ou apareceu carregador
 * sem cadastro.
 */

const ChargeSessionsFilter = (props) => (
  <Filter {...props}>
    <SearchInput source="stationCode" alwaysOn placeholder="Código do carregador" />
    <SelectInput
      label="Desfecho"
      source="status"
      choices={CHARGE_SESSION_STATUS_CHOICES}
      alwaysOn
    />
    <SearchInput source="customerCpf" placeholder="CPF do cliente" />
  </Filter>
);

const StatusChip = () => (
  <FunctionField
    label="Desfecho"
    sortable={false}
    render={record => {
      if (!record) { return null; }
      const chip = (
        <Chip
          size="small"
          label={translateStatus(record.status)}
          color={CHARGE_SESSION_STATUS_COLOR[record.status] ?? 'default'}
        />
      );
      // O motivo só existe quando não pontuou — é a resposta do "por quê", então vai junto
      // do desfecho em vez de numa coluna que ficaria vazia na maioria das linhas.
      return record.notPointedReason
        ? <Tooltip title={translateNotPointedReason(record.notPointedReason)}><span>{chip}</span></Tooltip>
        : chip;
    }}
  />
);

const ChargeSessionsReport = (props) => (
  <Permission permission={PERMISSIONS.ADMIN}>
    <List
      {...props}
      title="Recargas elétricas"
      exporter={false}
      bulkActionButtons={false}
      filters={<ChargeSessionsFilter />}
      pagination={<ListPagination />}
      perPage={25}
      // O backend ordena sempre por data desc (com id como desempate) e ignora `order`.
      // Marcar as colunas como não-ordenáveis evita oferecer um controle que não faz nada.
      sort={{ field: 'createdAt', order: 'DESC' }}
    >
      <>
        <ChargeSessionsSummary />
        <Datagrid bulkActionButtons={false} rowClick={false}>
          <DateField source="createdAt" sortable={false} label="Início" showTime />
          <TextField source="placeName" sortable={false} label="Posto" emptyText="—" />
          <TextField source="stationCode" label="Carregador" />
          <TextField source="customerName" sortable={false} label="Cliente" emptyText="—" />
          <FunctionField
            label="CPF"
            sortable={false}
            render={record => record?.customerCpf ? formatCpf(record.customerCpf) : '—'}
          />
          <NumberField
            source="kwh" sortable={false}
            label="kWh"
            textAlign="right"
            emptyText="—"
            options={{ minimumFractionDigits: 1, maximumFractionDigits: 3 }}
          />
          <NumberField
            source="value" sortable={false}
            label="Valor"
            textAlign="right"
            emptyText="—"
            options={{ style: 'currency', currency: 'BRL' }}
          />
          <NumberField
            source="points" sortable={false}
            label="Pontos"
            textAlign="right"
            emptyText="—"
            options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
          />
          <StatusChip />
          <FunctionField
            label="Webhook"
            sortable={false}
            // Quanto tempo entre abrir a sessão e o parceiro avisar que a recarga terminou.
            // Vazio = o aviso nunca chegou.
            render={record => record?.minutesToWebhook != null
              ? `${Number(record.minutesToWebhook).toFixed(0)} min`
              : '—'}
          />
        </Datagrid>
      </>
    </List>
  </Permission>
);

export default ChargeSessionsReport;
